/**
 * Canonical Role Slug Sets
 *
 * Single source of truth for staff/admin role classification.
 * Importable from both client and server code (no server dependencies).
 *
 * NOTE: These sets are for DB-backed role slugs used in role assignments.
 * They cover BOTH slug conventions: 'admin-platform' (legacy JWT metadata)
 * and 'platform-admin' (DB-seeded slug). Platform admin status can also
 * come from user_settings_permissions — always check isPlatformAdmin /
 * is_platform_admin flags in addition to role slugs.
 */

export const STAFF_ROLE_SLUGS = new Set([
  'officer', 'admin', 'supervisor', 'agent',
  'admin-platform', 'admin-tenant',
  'platform-admin',  // DB-seeded slug (vs 'admin-platform' in JWT metadata)
  'executive', 'owner', 'training-coordinator',
  'member', 'trainee',  // DB roles mapped from legacy 'officer'
]);

export const ADMIN_ROLE_SLUGS = new Set([
  'admin', 'admin-platform', 'admin-tenant',
  'platform-admin',  // DB-seeded slug
  'executive', 'owner',
]);

/**
 * Who may see TENANT-WIDE call analytics: cost totals, raw LLM error text and per-officer
 * rankings (Q-299 P0 #3, Prime ruling 2026-09-25). The admin tier plus supervisors, the same two
 * tiers the database gate admits (public.na_caller_can_read_tenant_analytics, migration 20261312,
 * via public.legacy_role_for_slugs). The database is the enforcement; the UI uses this set only
 * to show a clean "needs a supervisor" state instead of a refused request.
 */
export const TENANT_ANALYTICS_ROLE_SLUGS = new Set([...ADMIN_ROLE_SLUGS, 'supervisor']);

// Mirrors the DB legacy_role_for_slugs tier mapping used by route requiredRoles.
const LEGACY_ROLE_TIERS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['admin', ['platform-admin', 'admin-platform', 'admin-tenant', 'owner', 'admin', 'executive']],
  ['supervisor', ['supervisor']],
  ['officer', ['officer', 'agent', 'member', 'trainee', 'training-coordinator']],
  ['citizen', ['citizen']],
  ['guest', ['guest']],
];

export function legacyTierForSlug(slug: string): string | null {
  for (const [tier, slugs] of LEGACY_ROLE_TIERS) if (slugs.includes(slug)) return tier;
  return null;
}
